
import React, { useState, useEffect } from 'react';
import { CartItem, Product, User, Store, ShippingMethod, Coupon, VIPConfig } from '../types';
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag, ShieldCheck, Truck, CreditCard, Smartphone, Coins, Loader2, Lock, Sparkles, CheckCircle, Tag, Clock, Ticket, X, AlertCircle, Award } from 'lucide-react';
import { loyaltyService } from '../services/loyaltyService';
import { couponService } from '../services/couponService';
import { getSmartCheckoutIncentive } from '../services/geminiService';
import { storage } from '../lib/storage';
import { taxService } from '../services/taxService';
import { shippingService } from '../services/shippingService';
import { useCurrency } from '../contexts/CurrencyContext';
import { vipService } from '../services/vipService';

interface CartPageProps {
  items: CartItem[];
  user: User | null;
  onUpdateQuantity: (productId: string, delta: number) => void;
  onRemoveItem: (productId: string) => void;
  onCheckout: (paymentMethod: 'CARD' | 'UPI' | 'COINS', coinsToRedeem: number, shippingMethodId: string, couponId?: string) => void;
  onNavigateHome: () => void;
}

const CartPage: React.FC<CartPageProps> = ({ items, user, onUpdateQuantity, onRemoveItem, onCheckout, onNavigateHome }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'CARD' | 'UPI' | 'COINS'>('CARD');
  const [redeemCoins, setRedeemCoins] = useState(false);
  const [aiIncentive, setAiIncentive] = useState<string | null>(null);
  const [isLoadingIncentive, setIsLoadingIncentive] = useState(false);
  const [store, setStore] = useState<Store | null>(null);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [selectedShippingId, setSelectedShippingId] = useState<string>('');
  
  // VIP State
  const [vipConfig, setVipConfig] = useState<VIPConfig | null>(null);

  // Coupon States
  const [couponCode, setCouponCode] = useState('');
  const [activeCoupon, setActiveCoupon] = useState<Coupon | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const { format, convert } = useCurrency();

  useEffect(() => {
    if (items.length > 0) {
      fetchIncentive();
      const s = storage.getStoreConfig(items[0].product.storeId);
      setStore(s);
      const methods = shippingService.getStoreMethods(s.id);
      setShippingMethods(methods);
      if (methods.length > 0) setSelectedShippingId(methods[0].id);
      
      // Check VIP Tier
      if (user) {
        setVipConfig(vipService.getCurrentTier(s.id, user.id));
      }
    }
  }, [items.length, user]);

  const fetchIncentive = async () => {
    setIsLoadingIncentive(true);
    const text = await getSmartCheckoutIncentive(items, user?.coins || 0);
    setAiIncentive(text);
    setIsLoadingIncentive(false);
  };

  const subtotal = items.reduce((acc, item) => {
    const itemPrice = item.variant ? item.variant.price : item.product.price;
    return acc + (itemPrice * item.quantity);
  }, 0);

  // Discount Calculation
  const couponDiscount = activeCoupon ? couponService.calculateDiscount(activeCoupon, subtotal) : 0;
  
  // VIP Discount (applied after coupons)
  const subtotalAfterCoupon = subtotal - couponDiscount;
  const vipDiscount = vipConfig ? subtotalAfterCoupon * (vipConfig.discountPercentage / 100) : 0;
  
  const coinsAvailable = user?.coins || 0;
  const subtotalAfterVIP = subtotalAfterCoupon - vipDiscount;
  const maxRedeemableCoins = Math.min(coinsAvailable, Math.floor(subtotalAfterVIP / loyaltyService.COIN_VALUE));
  const coinsToRedeem = redeemCoins ? maxRedeemableCoins : 0;
  const coinDiscount = loyaltyService.calculateRedemptionValue(coinsToRedeem);

  const amountAfterDiscount = subtotalAfterVIP - coinDiscount;
  const taxRate = store?.taxConfig?.enabled ? (store.taxConfig.taxRate || 0) : 0;
  const taxAmount = taxService.calculateTax(amountAfterDiscount, taxRate);

  const selectedShipping = shippingMethods.find(m => m.id === selectedShippingId);
  const shippingCost = selectedShipping ? shippingService.calculateCost(selectedShipping, subtotal) : 0;
  
  const total = amountAfterDiscount + taxAmount + shippingCost;

  const handleApplyCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!couponCode.trim() || !store) return;
    
    setIsApplyingCoupon(true);
    setCouponError(null);
    
    await new Promise(resolve => setTimeout(resolve, 800));
    const result = couponService.validateCoupon(couponCode, store.id, subtotal);
    
    if (result.isValid && result.coupon) {
      setActiveCoupon(result.coupon);
      setCouponCode('');
    } else {
      setCouponError(result.error || 'Coupon apply nahi ho pa raha.');
    }
    setIsApplyingCoupon(false);
  };

  const handleRemoveCoupon = () => {
    setActiveCoupon(null);
    setCouponError(null);
  };

  const handleCheckout = () => {
    setIsProcessing(true);
    setTimeout(() => {
      onCheckout(paymentMethod, coinsToRedeem, selectedShippingId, activeCoupon?.id);
      setIsProcessing(false);
    }, 2500);
  };

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <div className="bg-gray-50 rounded-[40px] py-20 border border-dashed border-gray-200">
          <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-500 mb-8">Looks like you haven't added any gadgets yet.</p>
          <button 
            onClick={onNavigateHome}
            className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-bold shadow-xl shadow-blue-100 hover:scale-105 transition-all"
          >
            Start Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-4xl font-black text-gray-900 mb-10 tracking-tight">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          {aiIncentive && (
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 rounded-3xl text-white shadow-xl relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform"><Sparkles className="h-16 w-16" /></div>
               <div className="relative flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center shrink-0">
                     <Sparkles className="h-5 w-5" />
                  </div>
                  <p className="text-sm font-bold leading-relaxed">{aiIncentive}</p>
               </div>
            </div>
          )}

          {vipConfig && (
            <div className="bg-blue-50 border border-blue-100 p-6 rounded-3xl flex items-center justify-between shadow-sm animate-in slide-in-from-top-2">
               <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ backgroundColor: vipConfig.color }}>
                     <Award className="h-6 w-6" />
                  </div>
                  <div>
                     <p className="text-[10px] text-blue-600 font-black uppercase tracking-widest">VIP Privilege Applied</p>
                     <h4 className="text-lg font-black text-gray-900">{vipConfig.label} Status</h4>
                  </div>
               </div>
               <p className="text-blue-600 font-black text-xl">-{vipConfig.discountPercentage}% OFF</p>
            </div>
          )}

          <div className="space-y-4">
            <h2 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">1. Review Items</h2>
            <div className="space-y-4">
              {items.map((item, idx) => {
                const itemPrice = item.variant ? item.variant.price : item.product.price;
                return (
                  <div key={`${item.productId}-${item.variantId || idx}`} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col sm:flex-row gap-6 items-center hover:border-blue-100 transition-colors">
                    <div className="w-24 h-24 bg-gray-50 rounded-2xl overflow-hidden shrink-0 border border-gray-100">
                      <img src={item.product.images[0]} className="w-full h-full object-cover" alt="" />
                    </div>
                    <div className="flex-grow space-y-1 text-center sm:text-left">
                      <h3 className="font-bold text-lg text-gray-900">{item.product.name}</h3>
                      {item.variant && (
                        <p className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-1 justify-center sm:justify-start">
                          <Tag className="h-3 w-3" /> {item.variant.name}
                        </p>
                      )}
                      <p className="text-blue-600 font-black">{format(itemPrice)}</p>
                    </div>
                    <div className="flex items-center gap-4 bg-gray-50 px-4 py-2 rounded-2xl">
                      <button onClick={() => onUpdateQuantity(item.productId, -1)} disabled={item.quantity <= 1} className="p-1 hover:text-blue-600 disabled:opacity-30"><Minus className="h-4 w-4" /></button>
                      <span className="font-bold w-4 text-center">{item.quantity}</span>
                      <button onClick={() => onUpdateQuantity(item.productId, 1)} className="p-1 hover:text-blue-600"><Plus className="h-4 w-4" /></button>
                    </div>
                    <button onClick={() => onRemoveItem(item.productId)} className="text-red-500 hover:text-red-600 p-2"><Trash2 className="h-5 w-5" /></button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-6">
            <h2 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">2. Shipping Logistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {shippingMethods.map((method) => {
                 const cost = shippingService.calculateCost(method, subtotal);
                 const isSelected = selectedShippingId === method.id;
                 return (
                   <button 
                     key={method.id}
                     onClick={() => setSelectedShippingId(method.id)}
                     className={`p-6 rounded-[2rem] border-2 text-left transition-all ${isSelected ? 'border-blue-600 bg-blue-50 shadow-lg' : 'border-gray-100 hover:border-gray-200 bg-white'}`}
                   >
                      <div className="flex justify-between items-start mb-4">
                         <div className={`p-3 rounded-xl ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                            <Truck className="h-5 w-5" />
                         </div>
                         <p className="text-xl font-black text-gray-900">{cost === 0 ? 'FREE' : format(cost)}</p>
                      </div>
                      <h4 className="font-black text-gray-900">{method.name}</h4>
                      <p className="text-xs text-gray-500 font-medium mb-4">{method.description}</p>
                      <div className="flex items-center gap-2 text-[10px] font-black text-blue-600 uppercase tracking-widest">
                         <Clock className="h-3 w-3" /> ETA: {method.estimatedDays}
                      </div>
                   </button>
                 );
               })}
            </div>
          </div>

          <div className="space-y-6">
            <h2 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">3. Loyalty & Promotions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               {/* Coupons */}
               <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-6">
                  <div className="flex items-center gap-3">
                     <Ticket className="h-5 w-5 text-blue-600" />
                     <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Promo Codes</h3>
                  </div>

                  {activeCoupon ? (
                    <div className="bg-blue-50 p-6 rounded-3xl border border-blue-100 flex items-center justify-between">
                       <div>
                          <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Applied</p>
                          <p className="font-black text-gray-900 font-mono text-xl">{activeCoupon.code}</p>
                          <p className="text-xs text-blue-700 font-bold">-{activeCoupon.type === 'PERCENTAGE' ? `${activeCoupon.value}%` : format(activeCoupon.value)} OFF</p>
                       </div>
                       <button onClick={handleRemoveCoupon} className="p-3 bg-white text-gray-400 hover:text-red-500 rounded-xl transition-all shadow-sm">
                          <X className="h-4 w-4" />
                       </button>
                    </div>
                  ) : (
                    <form onSubmit={handleApplyCoupon} className="space-y-4">
                       <div className="relative">
                          <input 
                            type="text" 
                            value={couponCode}
                            onChange={e => setCouponCode(e.target.value)}
                            placeholder="Enter Code (e.g. LYNQ20)"
                            className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-black font-mono tracking-widest focus:ring-2 focus:ring-blue-600"
                          />
                          <button 
                            type="submit"
                            disabled={isApplyingCoupon}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-50"
                          >
                             {isApplyingCoupon ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                          </button>
                       </div>
                       {couponError && (
                         <div className="flex items-center gap-2 text-[10px] font-bold text-red-500 bg-red-50 px-4 py-2 rounded-xl animate-in shake duration-300">
                            <AlertCircle className="h-3 w-3" /> {couponError}
                         </div>
                       )}
                    </form>
                  )}
               </div>

               {/* Coins */}
               {user && (
                 <div className="bg-orange-50 border-2 border-orange-100 p-8 rounded-[2.5rem] flex flex-col justify-between space-y-6">
                    <div className="flex items-center gap-4">
                       <div className="w-12 h-12 bg-orange-600 text-white rounded-2xl flex items-center justify-center shadow-lg">
                          <Coins className="h-6 w-6" />
                       </div>
                       <div>
                          <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Lynq Coins</h3>
                          <p className="text-[10px] text-orange-700 font-bold uppercase">Available: {coinsAvailable} Coins</p>
                       </div>
                    </div>
                    {coinsAvailable > 0 && (
                      <button 
                        onClick={() => setRedeemCoins(!redeemCoins)}
                        className={`w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${
                          redeemCoins ? 'bg-orange-600 text-white shadow-xl' : 'bg-white text-orange-600 border-2 border-orange-100 hover:bg-orange-100'
                        }`}
                      >
                        {redeemCoins ? `Redeeming ${format(coinDiscount)}` : 'Apply Balance'}
                      </button>
                    )}
                 </div>
               )}
            </div>
          </div>

          <div className="space-y-6">
            <h2 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">4. Secure Payment</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
               {['CARD', 'UPI', 'COINS'].map((m) => (
                 <button 
                   key={m}
                   onClick={() => setPaymentMethod(m as any)}
                   className={`p-6 rounded-3xl border-2 flex flex-col items-center gap-3 transition-all ${paymentMethod === m ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-100 hover:border-gray-200'}`}
                 >
                   {m === 'CARD' && <CreditCard className="h-6 w-6" />}
                   {m === 'UPI' && <Smartphone className="h-6 w-6" />}
                   {m === 'COINS' && <Coins className="h-6 w-6" />}
                   <span className="text-xs font-black uppercase tracking-widest">{m}</span>
                 </button>
               ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-900 text-white p-10 rounded-[40px] shadow-2xl relative overflow-hidden">
            {isProcessing && (
              <div className="absolute inset-0 z-10 bg-gray-900/90 backdrop-blur-md flex flex-col items-center justify-center p-8 text-center space-y-4">
                 <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
                 <p className="text-sm font-black uppercase tracking-widest">Processing Transaction...</p>
              </div>
            )}
            <h3 className="text-xl font-bold mb-8">Order Summary</h3>
            <div className="space-y-5 text-sm">
              <div className="flex justify-between text-gray-400">
                <span>Subtotal</span>
                <span className="text-white font-bold">{format(subtotal)}</span>
              </div>
              
              {activeCoupon && (
                <div className="flex justify-between text-blue-400">
                  <span className="flex items-center gap-1.5"><Ticket className="h-3 w-3" /> Coupon ({activeCoupon.code})</span>
                  <span className="font-bold">-{format(couponDiscount)}</span>
                </div>
              )}

              {vipConfig && (
                <div className="flex justify-between text-indigo-400">
                  <span className="flex items-center gap-1.5"><Award className="h-3 w-3" /> VIP ({vipConfig.label})</span>
                  <span className="font-bold">-{format(vipDiscount)}</span>
                </div>
              )}

              {redeemCoins && (
                <div className="flex justify-between text-orange-400">
                  <span className="flex items-center gap-1.5"><Coins className="h-3 w-3" /> Coin Redemption</span>
                  <span className="font-bold">-{format(coinDiscount)}</span>
                </div>
              )}
              
              <div className="flex justify-between text-gray-400">
                <span>Estimated Tax ({taxRate}%)</span>
                <span className="text-white font-bold">{format(taxAmount)}</span>
              </div>
              
              <div className="flex justify-between text-gray-400">
                <span>Shipping ({selectedShipping?.name || 'Standard'})</span>
                <span className="text-white font-bold">{shippingCost === 0 ? 'FREE' : format(shippingCost)}</span>
              </div>
              
              <div className="border-t border-white/10 pt-6 flex justify-between items-end">
                <span className="text-lg font-bold">Total</span>
                <span className="text-4xl font-black">{format(total)}</span>
              </div>
            </div>

            <button 
              onClick={handleCheckout}
              disabled={isProcessing}
              className="w-full mt-10 bg-blue-600 hover:bg-blue-700 py-6 rounded-2xl font-black text-lg flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50"
            >
              Confirm Purchase <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
